import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Avatar,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Assessment,
  Close,
  Person,
  DateRange,
  NotificationsActive
} from '@mui/icons-material';
import api from '../../config/axiosConfig';

// Etapas para las que sí existe un responsable puntual a quien recordarle -- "Asignado y
// Programado" es más una condición de la propia orden que una tarea de una persona concreta,
// así que no lleva botón de recordatorio.
const REMINDABLE_STAGES = new Set(['uipc_registrada', 'archivos_subidos', 'evaluacion_inspeccion']);

const PipcProgressDialog = ({ open, onClose, quotationId, companyName }) => {
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingStage, setSendingStage] = useState(null);
  const [loadingPreviewStage, setLoadingPreviewStage] = useState(null);
  // Confirmación previa: null = cerrada; si no, { stageKey, stageLabel, recipients: [{id,name}] }
  const [confirmReminder, setConfirmReminder] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Paso 1: consulta a quién se le mandaría (sin mandar nada todavía) y abre la confirmación.
  // detailId opcional: recordatorio solo para ese ente (si no, a los pendientes de toda la orden)
  const reminderKey = (stageKey, detailId) => (detailId ? `${stageKey}:${detailId}` : stageKey);

  const handleOpenReminderConfirm = async (stageKey, detailId = null) => {
    setLoadingPreviewStage(reminderKey(stageKey, detailId));
    try {
      const resp = await api.get(`/pip-related/quotation/${quotationId}/remind-preview`, {
        params: { stage: stageKey, ...(detailId ? { detailId } : {}) }
      });
      const recipients = resp.data?.recipients || [];
      if (recipients.length === 0) {
        setSnackbar({ open: true, message: 'No hay destinatarios activos para esta etapa.', severity: 'warning' });
        return;
      }
      setConfirmReminder({ stageKey, detailId, stageLabel: resp.data?.stageLabel, recipients });
    } catch (err) {
      console.error('Error al consultar destinatarios del recordatorio:', err);
      setSnackbar({ open: true, message: 'No se pudo consultar a quién enviar el recordatorio.', severity: 'error' });
    } finally {
      setLoadingPreviewStage(null);
    }
  };

  // Paso 2: el usuario ya vio a quién y confirmó -- ahora sí se manda la notificación interna.
  const handleConfirmSendReminder = async () => {
    if (!confirmReminder) return;
    const { stageKey, detailId } = confirmReminder;
    setConfirmReminder(null);
    setSendingStage(reminderKey(stageKey, detailId));
    try {
      const resp = await api.post(`/pip-related/quotation/${quotationId}/remind`, {
        stage: stageKey, ...(detailId ? { detailId } : {})
      });
      const sent = resp.data?.sent ?? 0;
      setSnackbar({
        open: true,
        message: sent > 0
          ? `Recordatorio enviado a ${sent} persona${sent === 1 ? '' : 's'}.`
          : 'No hay destinatarios activos para esta etapa.',
        severity: sent > 0 ? 'success' : 'warning'
      });
    } catch (err) {
      console.error('Error al enviar recordatorio PIPC:', err);
      setSnackbar({ open: true, message: 'No se pudo enviar el recordatorio.', severity: 'error' });
    } finally {
      setSendingStage(null);
    }
  };

  useEffect(() => {
    if (open && quotationId) {
      fetchProgress();
    }
  }, [open, quotationId]);

  const fetchProgress = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pip-related/quotation/${quotationId}/progress`);
      setProgressData(response.data);
    } catch (err) {
      console.error('Error al obtener progreso PIPC:', err);
      setProgressData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fechas "puras" (YYYY-MM-DD) del backend se arman en LOCAL para no recorrerlas un día por
  // la conversión de zona horaria -- mismo criterio que TablaSeguimiento.
  const fmtDate = (val) => {
    if (!val) return null;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
      const [y, m, d] = val.substring(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    const d = new Date(val);
    return isNaN(d) ? val : d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getProgressColor = (percentage) => {
    if (percentage === 100) return '#4caf50';
    if (percentage >= 60) return '#2196f3';
    if (percentage >= 40) return '#ff9800';
    return '#f44336';
  };

  const getProgressLabel = (percentage) => {
    if (percentage === 100) return { label: 'Completo', color: 'success' };
    if (percentage >= 60) return { label: 'En Progreso', color: 'primary' };
    if (percentage >= 40) return { label: 'Iniciado', color: 'warning' };
    return { label: 'Pendiente', color: 'error' };
  };

  const general = progressData?.general;
  const percentage = general?.percentage ?? 0;
  const progressColor = getProgressColor(percentage);
  const { label: statusLabel, color: statusColor } = getProgressLabel(percentage);
  const porEnte = progressData?.porEnte || [];
  // "OT" = general es la orden completa (Admin/Coordinador); "ASIGNADOS" = solo el agregado
  // de los entes que este usuario tiene asignados (Ejecutivo de Operaciones restringido).
  const isScoped = progressData?.scope === 'ASIGNADOS';
  const canRemind = !isScoped;
  const generalTitle = isScoped ? 'Avance operativo de tus entes asignados' : 'Avance operativo de la orden completa';
  // "Listo para generar": campos requeridos del PIPC + documentos, por ente (va aparte del avance operativo)
  const readiness = progressData?.readiness;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 2
      }}>
        <Assessment />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
            Avance del PIPC
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {companyName || 'Empresa'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : progressData ? (
          <>
            {/* Resumen de porcentaje */}
            <Box sx={{
              p: 3,
              mb: 2,
              borderRadius: 2,
              bgcolor: '#f8f9fa',
              border: `2px solid ${progressColor}`,
              textAlign: 'center'
            }}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', color: progressColor, lineHeight: 1 }}>
                {percentage}%
              </Typography>
              <Chip
                label={statusLabel}
                color={statusColor}
                size="small"
                sx={{ mt: 1, fontWeight: 'bold' }}
              />
              <Box sx={{ mt: 2, mx: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 5 }
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {general?.completedCount} de {general?.totalCount} etapas operativas completadas ({isScoped ? 'tus entes asignados' : 'orden completa'})
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Lista de ítems */}
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#555' }}>
              {generalTitle}
            </Typography>
            <List dense disablePadding>
              {general?.items?.map((item, index) => (
                <ListItem
                  key={item.key}
                  sx={{
                    px: 1.5,
                    py: 1,
                    mb: 0.5,
                    borderRadius: 2,
                    bgcolor: item.completed ? 'rgba(76,175,80,0.08)' : 'rgba(0,0,0,0.03)',
                    border: `1px solid ${item.completed ? 'rgba(76,175,80,0.25)' : 'rgba(0,0,0,0.08)'}`,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {item.completed
                      ? <CheckCircle sx={{ color: '#4caf50', fontSize: 22 }} />
                      : <RadioButtonUnchecked sx={{ color: '#bdbdbd', fontSize: 22 }} />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: item.completed ? 600 : 400 }}>
                        {index + 1}. {item.label}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {item.description}
                      </Typography>
                    }
                  />
                  {canRemind && !item.completed && REMINDABLE_STAGES.has(item.key) && (
                    <Tooltip title="Enviar recordatorio al responsable">
                      <span>
                        <IconButton
                          size="small"
                          disabled={sendingStage === item.key || loadingPreviewStage === item.key}
                          onClick={() => handleOpenReminderConfirm(item.key)}
                          sx={{ mr: 0.5 }}
                        >
                          {(sendingStage === item.key || loadingPreviewStage === item.key)
                            ? <CircularProgress size={16} />
                            : <NotificationsActive fontSize="small" sx={{ color: '#1976d2' }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Chip
                    label={item.completed ? 'Listo' : 'Pendiente'}
                    size="small"
                    variant="outlined"
                    color={item.completed ? 'success' : 'default'}
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </ListItem>
              ))}
            </List>

            {readiness && readiness.total > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#555', flexGrow: 1 }}>
                    Listo para generar el PIPC
                  </Typography>
                  <Chip
                    size="small"
                    label={readiness.ready ? '✓ Listo' : `${readiness.readyCount} de ${readiness.total} entes listos`}
                    color={readiness.ready ? 'success' : 'warning'}
                    sx={{ fontWeight: 700 }}
                  />
                  {canRemind && readiness.docsReadyCount < readiness.total && (
                    <Tooltip title="Recordar la subida de documentos a los responsables de todos los entes pendientes">
                      <span>
                        <IconButton size="small"
                          disabled={sendingStage === 'archivos_subidos' || loadingPreviewStage === 'archivos_subidos'}
                          onClick={() => handleOpenReminderConfirm('archivos_subidos')}>
                          {(sendingStage === 'archivos_subidos' || loadingPreviewStage === 'archivos_subidos')
                            ? <CircularProgress size={16} />
                            : <NotificationsActive fontSize="small" sx={{ color: '#1976d2' }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Campos requeridos del PIPC y documentos de cumplimiento de cada ente (mismo criterio que "Validación de Campos").
                </Typography>
                <List dense disablePadding>
                  {readiness.entities.map((e) => (
                    <ListItem key={String(e.branchId)} sx={{
                      px: 1.5, py: 1, mb: 0.5, borderRadius: 2,
                      bgcolor: e.pipcReady && e.docsReady ? 'rgba(76,175,80,0.08)' : 'rgba(0,0,0,0.03)',
                      border: `1px solid ${e.pipcReady && e.docsReady ? 'rgba(76,175,80,0.25)' : 'rgba(0,0,0,0.08)'}`,
                    }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {e.pipcReady && e.docsReady
                          ? <CheckCircle sx={{ color: '#4caf50', fontSize: 22 }} />
                          : <RadioButtonUnchecked sx={{ color: '#bdbdbd', fontSize: 22 }} />}
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="body2" sx={{ fontWeight: 600 }}>{e.entityName}</Typography>}
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Campos requeridos PIPC: {e.pipcReady ? 'completos' : `${e.pipcPercentage ?? 0}%`}
                            {' · '}Documentos: {e.docsUploaded ?? 0}/{e.docsRequired ?? 0}
                          </Typography>
                        }
                      />
                      {canRemind && !e.docsReady && e.workOrderDetailId && (
                        <Tooltip title={`Recordar la subida de documentos solo al responsable de ${e.entityName}`}>
                          <span>
                            <IconButton size="small" sx={{ mr: 0.5 }}
                              disabled={sendingStage === reminderKey('archivos_subidos', e.workOrderDetailId)
                                || loadingPreviewStage === reminderKey('archivos_subidos', e.workOrderDetailId)}
                              onClick={() => handleOpenReminderConfirm('archivos_subidos', e.workOrderDetailId)}>
                              {(sendingStage === reminderKey('archivos_subidos', e.workOrderDetailId)
                                || loadingPreviewStage === reminderKey('archivos_subidos', e.workOrderDetailId))
                                ? <CircularProgress size={16} />
                                : <NotificationsActive fontSize="small" sx={{ color: '#1976d2' }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Chip
                        label={e.pipcReady && e.docsReady ? 'Listo' : 'Pendiente'}
                        size="small" variant="outlined"
                        color={e.pipcReady && e.docsReady ? 'success' : 'default'}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {porEnte.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#555' }}>
                  Avance por ente
                </Typography>
                <List dense disablePadding>
                  {porEnte.map((ente) => {
                    const enteColor = getProgressColor(ente.percentage);
                    return (
                      <Box
                        key={ente.workOrderDetailId}
                        sx={{ p: 1.5, mb: 1, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {ente.entityName}
                          </Typography>
                          <Chip
                            label={`${ente.percentage}% (${ente.completedCount}/${ente.totalCount})`}
                            size="small"
                            sx={{ fontWeight: 700, bgcolor: enteColor, color: 'white' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Person sx={{ fontSize: 15, color: '#78909c' }} />
                            <Typography variant="caption" color="text.secondary">
                              {ente.assignedUserName || 'Sin ejecutivo asignado'}
                            </Typography>
                          </Box>
                          {(ente.scheduledDate || ente.estimatedCompletionDate) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DateRange sx={{ fontSize: 15, color: '#78909c' }} />
                              <Typography variant="caption" color="text.secondary">
                                {fmtDate(ente.scheduledDate) || 'Sin inicio'} — {fmtDate(ente.estimatedCompletionDate) || 'Sin fin'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={ente.percentage}
                          sx={{
                            height: 6, borderRadius: 3, bgcolor: '#e0e0e0', mb: 1,
                            '& .MuiLinearProgress-bar': { bgcolor: enteColor, borderRadius: 3 }
                          }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {ente.items?.map(item => (
                            <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {item.completed
                                ? <CheckCircle sx={{ color: '#4caf50', fontSize: 16 }} />
                                : <RadioButtonUnchecked sx={{ color: '#bdbdbd', fontSize: 16 }} />}
                              <Typography variant="caption" color={item.completed ? 'text.primary' : 'text.secondary'}>
                                {item.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </List>
              </>
            )}
          </>
        ) : (
          <Box textAlign="center" py={3}>
            <Typography color="text.secondary">No se pudo cargar el progreso.</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} startIcon={<Close />} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>

    {/* Confirmación previa: antes de mandar la notificación se muestra a quién le llegaría. */}
    <Dialog
      open={!!confirmReminder}
      onClose={() => setConfirmReminder(null)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 2.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.18)', borderRadius: '50%', width: 40, height: 40 }}>
          <NotificationsActive />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
            Enviar recordatorio
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Notificación interna del sistema
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Se enviará una notificación sobre{' '}
          <Box component="span" sx={{ fontWeight: 700, color: '#1976d2' }}>{confirmReminder?.stageLabel}</Box> a:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {(confirmReminder?.recipients || []).map((r) => (
            <Box
              key={r.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}
            >
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#e3f2fd', color: '#1976d2', fontSize: '0.8rem', fontWeight: 700 }}>
                {r.name?.trim()?.charAt(0)?.toUpperCase() || <Person sx={{ fontSize: 18 }} />}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{r.name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{r.email}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={() => setConfirmReminder(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmSendReminder}
          variant="contained"
          startIcon={<NotificationsActive />}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2.5 }}
        >
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default PipcProgressDialog;
